/**
 * Activity-feed kind chip color pairs (DNKADM-0269) — moved here from
 * `src/features/activity/activityKind.ts` (the `AUDIT_TINT`-style icon-bubble
 * tints) so the feature dir stays literal-free (the DNKADM-0269 ESLint guard
 * bans raw colors in `src/features/**`; `src/theme/**` owns them). Also backs
 * the purchase-type badge inline in `ActivityLog.tsx`, which reuses the
 * `purchase` pair verbatim (same purple family, same chip).
 *
 * LIGHT values are byte-identical to the pre-0269 literals (the 0266 identity
 * constraint). DARK values follow the shared translucent-tint + pastel-ink
 * ramp used across the sibling `transactionLedger`/`configAudit` maps — the
 * same four-family palette (green/purple/amber/red) recurs across the three
 * because the underlying feeds share the same design-handoff chip colors.
 */

/** One kind's icon-bubble tint/ink pair + Material Symbols glyph. */
interface ActivityKindStyle {
  bg: string;
  fg: string;
  icon: string;
}

export interface ActivityKindMaps {
  purchase: ActivityKindStyle;
  credit: ActivityKindStyle;
  membership: ActivityKindStyle;
  reservation: ActivityKindStyle;
  /** Neutral tint/glyph for an unrecognized or absent kind. */
  fallback: ActivityKindStyle;
}

const LIGHT: ActivityKindMaps = {
  purchase: { bg: '#E9E6F2', fg: '#5B4B9E', icon: 'shopping_cart' },
  credit: { bg: '#E5F1E9', fg: '#2E7D52', icon: 'account_balance_wallet' },
  membership: { bg: '#E2EDEF', fg: '#256A6E', icon: 'workspace_premium' },
  reservation: { bg: '#F1E8DA', fg: '#8A5A12', icon: 'event' },
  fallback: { bg: '#EDEAE4', fg: 'rgba(0,0,0,0.55)', icon: 'history' },
};

const DARK: ActivityKindMaps = {
  purchase: { bg: 'rgba(91,75,158,0.22)', fg: '#B3ABCB', icon: 'shopping_cart' },
  credit: { bg: 'rgba(46,125,82,0.24)', fg: '#7FD3A3', icon: 'account_balance_wallet' },
  membership: { bg: 'rgba(37,106,110,0.26)', fg: '#8FD0D4', icon: 'workspace_premium' },
  reservation: { bg: 'rgba(138,90,18,0.28)', fg: '#E8C27A', icon: 'event' },
  fallback: { bg: 'rgba(184,192,186,0.18)', fg: '#8F9992', icon: 'history' },
};

/** Per-mode activity-kind chip pairs. Light === the pre-0269 literals. */
export function buildActivityKindMaps(mode: 'light' | 'dark'): ActivityKindMaps {
  return mode === 'dark' ? DARK : LIGHT;
}
